import React, { useState } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Settings, Edit2, School, Key, CheckCircle2, AlertCircle, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { useAppNavigation } from '../../../app/hooks/useAppNavigation';
import type { Classroom, TeacherAccount } from '../../../accounts';
import { prepareAccountDeletion, executeAccountDeletion, type AccountDeletionDecision, type AccountDeletionPreparation } from '../../../accounts';
import { usePasswordChange } from '../../shared/usePasswordChange';
interface SettingsPageProps {
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  onNavigate?: (route: string) => void;
}
export function SettingsPage({
  activeAccount,
  classrooms = [],
  onNavigate
}: SettingsPageProps) {
  // Singleton store — all consumers share the same state (Zustand refactor s81)
  const {
    accounts,
    clearActiveAccount,
    updateAccountNameById,
    updateAccountEmail,
    updateTeacherDetails,
    updatePassword
  } = useAppStore();
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(activeAccount?.displayName || '');
  const [editEmail, setEditEmail] = useState(activeAccount?.email || '');
  const [editSchool, setEditSchool] = useState(activeAccount?.schoolName || '');
  // Password change — shared hook
  const pw = usePasswordChange({
    accountId: activeAccount.id,
    passwordHash: activeAccount.passwordHash,
    role: 'teacher',
    updatePassword
  });
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized: true,
    switchAccount: () => {},
    setViewingLearner: () => {},
    clearActiveAccount
  });
  // Account deletion state
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deletionPrep, setDeletionPrep] = useState<AccountDeletionPreparation | null>(null);
  const [deletionDecisions, setDeletionDecisions] = useState<Record<string, AccountDeletionDecision>>({});
  const handleStartDeletion = () => {
    const prep = prepareAccountDeletion(accounts, activeAccount.id);
    setDeletionPrep(prep);
    // Initialize decisions
    const initialDecisions: Record<string, AccountDeletionDecision> = {};
    prep.ownedClassrooms.forEach(c => {
      initialDecisions[c.classroom.id] = {
        classroomId: c.classroom.id,
        action: 'delete' // Default action
      };
    });
    setDeletionDecisions(initialDecisions);
    setIsDeletingAccount(true);
  };
  const handleConfirmDeletion = () => {
    if (!deletionPrep) return;
    // Ensure all classrooms have a valid decision
    const decisions = Object.values(deletionDecisions);
    const isValid = decisions.every(d => {
      if (d.action === 'transfer') return !!d.newTeacherId;
      return true;
    });
    if (!isValid) return;
    // Execute deletion
    executeAccountDeletion(accounts, activeAccount.id, decisions);
    // Clear active account and navigate to login
    clearActiveAccount();
    nav.navigateToLogin();
  };
  const handleSaveProfile = () => {
    if (editName.trim()) {
      updateAccountNameById(activeAccount.id, editName.trim());
    }
    if (editEmail.trim() !== (activeAccount.email || '')) {
      updateAccountEmail(activeAccount.id, editEmail.trim());
    }
    if (editSchool.trim() !== (activeAccount.schoolName || '')) {
      updateTeacherDetails(activeAccount.id, {
        schoolName: editSchool.trim()
      });
    }
    setIsEditingProfile(false);
  };
  return <PageLayout title="Instellingen" description="Beheer je docentaccount en voorkeuren" data-id="element-10519" data-mp="1f8qrugzhc6ao">
      
      <div className="max-w-3xl mx-auto space-y-6" data-id="element-10520" data-mp="1aaz4m09s58ct">
        {/* Account Info */}
        <SettingsSection title="Account Instellingen" description="Beheer je persoonlijke gegevens" icon={Settings} accentColor="bg-violet-100 text-violet-600" action={!isEditingProfile ? <Button onClick={() => {
        setEditName(activeAccount?.displayName || '');
        setEditEmail(activeAccount?.email || '');
        setEditSchool(activeAccount?.schoolName || '');
        setIsEditingProfile(true);
      }} variant="ghost" size="sm" data-id="element-10522" data-mp="6eesjtve94i0">
            
                <Edit2 className="w-4 h-4 mr-2" data-id="element-10523" data-mp="3eu87436605wf" /> Bewerk
              </Button> : null} data-id="element-10521" data-mp="1dr3hdqq6xqf">
          
          <div className="space-y-6" data-id="element-10524" data-mp="3jrq7v6a13etc">
            {isEditingProfile ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-10525" data-mp="3nstnbro5z8u1">
                <div data-id="element-10526" data-mp="3sjs7x3g6qt33">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10527" data-mp="3sk1yot92149u">
                    Naam
                  </label>
                  <Input variant="admin" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Jouw naam" autoFocus data-id="element-10528" data-mp="2w1e2o3663w6g" />
                
                </div>
                <div data-id="element-10529" data-mp="2six437m6mafs">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10530" data-mp="2sjgdu11ohsiq">
                    E-mailadres
                  </label>
                  <Input variant="admin" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="jouw@email.nl" data-id="element-10531" data-mp="2jj1omqllft9y" />
                
                </div>
                <div className="sm:col-span-2" data-id="element-10532" data-mp="2emjl1u2020z7">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10533" data-mp="29m5nj5y0y9ff">
                    School
                  </label>
                  <Input variant="admin" value={editSchool} onChange={e => setEditSchool(e.target.value)} placeholder="Naam van je school" data-id="element-10534" data-mp="24saydmznh28y" />
                
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 mt-2" data-id="element-10535" data-mp="1vvaentnizsbu">
                  <Button onClick={() => setIsEditingProfile(false)} variant="secondary" data-id="element-10536" data-mp="1qtkyy0pcyqhf">
                  
                    Annuleren
                  </Button>
                  <button onClick={handleSaveProfile} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-10537" data-mp="1c7xhinaji029">
                  
                    Opslaan
                  </button>
                </div>
              </div> : <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-10538" data-mp="178b0e2rlbzo6">
                  <div data-id="element-10539" data-mp="17anrb5fbtkdd">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10540" data-mp="1uxvlar7b1703">
                      Naam
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-10541" data-mp="1zwyrwg2xnla6">
                      {activeAccount?.displayName}
                    </div>
                  </div>
                  <div data-id="element-10542" data-mp="1q5m2dv4lx77b">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10543" data-mp="2dxj9j8x43b23">
                      E-mailadres
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-10544" data-mp="2ipkjgo6t4poy">
                      {activeAccount?.email || <span className="text-slate-400 italic" data-id="element-10545" data-mp="2a7d830pd6y1">
                          Niet ingesteld
                        </span>}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100" data-id="element-10546" data-mp="bz7fpweeykgz">
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 max-w-sm" data-id="element-10547" data-mp="c1tvgjbgk6aa">
                    <div className="p-2 bg-white rounded-lg border border-slate-200 text-slate-500 shadow-sm" data-id="element-10548" data-mp="3fgfycigytocr">
                      <School className="w-5 h-5" data-id="element-10549" data-mp="3tgqnnh2mmuvt" />
                    </div>
                    <div data-id="element-10550" data-mp="3pen9gkokpc6u">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-10551" data-mp="3pe3zpr92tu3w">
                        School
                      </div>
                      <div className="font-medium text-slate-900" data-id="element-10552" data-mp="18rujgg406iqj">
                        {activeAccount?.schoolName || 'Niet opgegeven'}
                      </div>
                    </div>
                  </div>
                </div>
              </>}
          </div>
        </SettingsSection>

        {/* Password Change */}
        <SettingsSection title="Wachtwoord" description="Wijzig je wachtwoord" icon={Key} accentColor="bg-violet-100 text-violet-600" action={!pw.isChangingPassword ? <Button onClick={() => pw.setIsChangingPassword(true)} variant="ghost" size="sm" data-id="element-10554" data-mp="3cxcikayahm8f">
            
                <Edit2 className="w-4 h-4 mr-2" data-id="element-10555" data-mp="3gzpph2orvq6s" /> Wijzig
              </Button> : null} data-id="element-10553" data-mp="385kxuh1teaq7">
          
          {pw.isChangingPassword ? <form onSubmit={pw.handleChangePassword} className="space-y-4" data-id="element-10556" data-mp="3qvswomyhef0l">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-10557" data-mp="3qtpxbcbfv9j7">
                <div className="sm:col-span-2" data-id="element-10558" data-mp="3vtknfdne3v37">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10559" data-mp="1whrsrno2r1tn">
                    Huidig wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.currentPassword} onChange={e => pw.setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-10560" data-mp="21hnx5o0oov9q" />
                
                </div>
                <div data-id="element-10561" data-mp="dtgrq2ntui9k">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10562" data-mp="dsxa6n239vw2">
                    Nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.newPassword} onChange={e => pw.setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-10563" data-mp="4v4sxdct4e61" />
                
                </div>
                <div data-id="element-10564" data-mp="xihhtu6fwy2i">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10565" data-mp="xkc1z69w0m5j">
                    Bevestig nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.confirmPassword} onChange={e => pw.setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-10566" data-mp="nktjku9ml8fl" />
                
                </div>
              </div>

              {pw.passwordError && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3 rounded-lg border border-rose-100" data-id="element-10567" data-mp="38aajzayk5guz">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-10568" data-mp="2nij7jobc9ikr" />
                  <p data-id="element-10569" data-mp="2imvgf3daqfcm">{pw.passwordError}</p>
                </div>}

              {pw.passwordSuccess && <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100" data-id="element-10570" data-mp="2xbi7g2cacvjo">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-10571" data-mp="2shnes9epd68g" />
                  <p data-id="element-10572" data-mp="369tyu6kz7xd6">{pw.passwordSuccess}</p>
                </div>}

              <div className="flex justify-end gap-2 pt-2" data-id="element-10573" data-mp="31cjznz7sc7c5">
                <Button type="button" onClick={() => pw.resetPasswordForm()} variant="secondary" data-id="element-10574" data-mp="31e4s9jajhg7d">
                
                  Annuleren
                </Button>
                <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-10575" data-mp="3bbj8bhsjw2z5">
                
                  Wachtwoord Wijzigen
                </button>
              </div>
            </form> : <div className="text-sm text-slate-500" data-id="element-10576" data-mp="3sjahboxlo2q9">
              Je wachtwoord is veilig opgeslagen. Klik op 'Wijzig' om een nieuw
              wachtwoord in te stellen.
            </div>}
        </SettingsSection>

        {/* Account Deletion */}
        <SettingsSection title="Account Verwijderen" description="Verwijder je account en beheer je klassen" icon={Trash2} accentColor="bg-rose-100 text-rose-600" data-id="element-10577" data-mp="3irmpvo11mlos">
          
          {!isDeletingAccount ? <div data-id="element-10578" data-mp="39v5h2rg5izw7">
              <p className="text-sm text-slate-600 mb-4" data-id="element-10579" data-mp="3duo0tjw8l4xg">
                Als je je account verwijdert, verlies je toegang tot al je
                gegevens. Je kunt kiezen wat er met je klassen moet gebeuren.
              </p>
              <Button onClick={handleStartDeletion} variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-id="element-10580" data-mp="34t6dphkew2z9">
              
                Account verwijderen...
              </Button>
            </div> : <div className="space-y-6" data-id="element-10581" data-mp="131a2k925ov50">
              <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl" data-id="element-10582" data-mp="1308odvpufsj2">
                <h4 className="font-bold text-rose-900 mb-2" data-id="element-10583" data-mp="11mqrkjq32vzt">
                  Weet je zeker dat je je account wilt verwijderen?
                </h4>
                <p className="text-sm text-rose-800" data-id="element-10584" data-mp="rmgfx3jp5wmr">
                  Dit kan niet ongedaan worden gemaakt.
                  {deletionPrep && deletionPrep.ownedClassrooms.length > 0 && <span className="block mt-2 font-bold" data-id="element-10585" data-mp="1kbxfqm9ncft0">
                      Je beheert {deletionPrep.ownedClassrooms.length} klassen.
                      Maak hieronder een keuze per klas:
                    </span>}
                </p>
              </div>

              {deletionPrep && deletionPrep.ownedClassrooms.length > 0 && <div className="space-y-4" data-id="element-10586" data-mp="15nkizn2qisxg">
                  {deletionPrep.ownedClassrooms.map(c => {
              const decision = deletionDecisions[c.classroom.id];
              const hasTransferOptions = c.coTeachers.length > 0 || c.schoolColleagues.length > 0;
              return <div key={c.classroom.id} className="p-4 border border-slate-200 rounded-xl bg-white" data-id="element-10587" data-mp="1y9vjyyu96vus">
                    
                        <div className="flex items-center justify-between mb-3" data-id="element-10588" data-mp="2al5zdw9c3p1f">
                          <div className="font-bold text-slate-900" data-id="element-10589" data-mp="2flafvma8sjrn">
                            {c.classroom.name}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-10590" data-mp="2klortfeysduy">
                            {c.studentCount} leerlingen, {c.coTeachers.length}{' '}
                            co-docenten
                          </div>
                        </div>

                        <div className="space-y-2" data-id="element-10591" data-mp="2ybs9dt2wfje2">
                          {hasTransferOptions && <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-id="element-10592" data-mp="2yduwo5co2yf3">
                              <input type="radio" name={`action-${c.classroom.id}`} checked={decision?.action === 'transfer'} onChange={() => setDeletionDecisions(prev => ({
                      ...prev,
                      [c.classroom.id]: {
                        ...prev[c.classroom.id],
                        action: 'transfer',
                        newTeacherId: c.coTeachers[0]?.id || c.schoolColleagues[0]?.id
                      }
                    }))} className="mt-1 text-indigo-600" data-id="element-10593" data-mp="389y9c555afga" />
                        
                              <div className="flex-1" data-id="element-10594" data-mp="imtg5e9wjiph">
                                <div className="text-sm font-medium text-slate-900" data-id="element-10595" data-mp="3r1abhleu1yf4">
                                  Draag over aan:
                                </div>
                                {decision?.action === 'transfer' && <select value={decision.newTeacherId || ''} onChange={e => setDeletionDecisions(prev => ({
                        ...prev,
                        [c.classroom.id]: {
                          ...prev[c.classroom.id],
                          newTeacherId: e.target.value
                        }
                      }))} className="mt-2 w-full text-sm border-slate-200 rounded-lg" data-id="element-10596" data-mp="3wccdo9sogpf">
                            
                                    {c.coTeachers.length > 0 && <optgroup label="Co-docenten" data-id="element-10597" data-mp="1nsry607m8s9d">
                                        {c.coTeachers.map(t => <option key={t.id} value={t.id} data-id="element-10598" data-mp="1nqf80zrpfbl8">
                                            {t.name}
                                          </option>)}
                                      </optgroup>}
                                    {c.schoolColleagues.length > 0 && <optgroup label="Collega's" data-id="element-10599" data-mp="21lhj01berr4w">
                                        {c.schoolColleagues.map(t => <option key={t.id} value={t.id} data-id="element-10600" data-mp="26hy9h262yhl5">
                                            {t.name}
                                          </option>)}
                                      </optgroup>}
                                  </select>}
                              </div>
                            </label>}

                          <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-id="element-10601" data-mp="2kh7o3xen13n0">
                            <input type="radio" name={`action-${c.classroom.id}`} checked={decision?.action === 'delete'} onChange={() => setDeletionDecisions(prev => ({
                      ...prev,
                      [c.classroom.id]: {
                        ...prev[c.classroom.id],
                        action: 'delete',
                        newTeacherId: undefined
                      }
                    }))} className="mt-1 text-indigo-600" data-id="element-10602" data-mp="2z5hgzljgwtpo" />
                        
                            <div className="text-sm font-medium text-slate-900" data-id="element-10603" data-mp="2bg59qr7pavq0">
                              Verwijder klas
                            </div>
                          </label>

                          <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-id="element-10604" data-mp="26epmqtlyqf4z">
                            <input type="radio" name={`action-${c.classroom.id}`} checked={decision?.action === 'orphan'} onChange={() => setDeletionDecisions(prev => ({
                      ...prev,
                      [c.classroom.id]: {
                        ...prev[c.classroom.id],
                        action: 'orphan',
                        newTeacherId: undefined
                      }
                    }))} className="mt-1 text-indigo-600" data-id="element-10605" data-mp="1xg499cvclqb2" />
                        
                            <div className="text-sm font-medium text-slate-900" data-id="element-10606" data-mp="lz1g7f27tusd">
                              Bewaar als verweesd (admin handelt af)
                            </div>
                          </label>
                        </div>
                      </div>;
            })}
                </div>}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-10607" data-mp="3uf36ve1abxgx">
                <Button onClick={() => setIsDeletingAccount(false)} variant="secondary" data-id="element-10608" data-mp="305l81ij9cdh">
                
                  Annuleren
                </Button>
                <Button onClick={handleConfirmDeletion} className="bg-rose-600 hover:bg-rose-700 text-white px-6 py-2 rounded-xl text-sm font-bold transition-all" disabled={deletionPrep && Object.values(deletionDecisions).some(d => d.action === 'transfer' && !d.newTeacherId)} data-id="element-10609" data-mp="32n5h03enew9a">
                
                  Bevestig verwijdering
                </Button>
              </div>
            </div>}
        </SettingsSection>

        {/* Klas-beheer staat in een aparte pagina (Beheer) — geen duplicatie hier. */}
        <div className="text-xs text-slate-400 text-center pt-2" data-id="element-10610" data-mp="24zpwzohsa479">
          Wil je klassen of leerlingen beheren?{' '}
          <button onClick={() => onNavigate?.('management')} className="text-indigo-600 hover:text-indigo-700 font-bold underline-offset-2 hover:underline" data-id="element-10611" data-mp="2iyfuh58im5w5">
            
            Ga naar Beheer →
          </button>
        </div>
      </div>
    </PageLayout>;
}